/** Status → coloured badge mappers, shared across the tables and detail views. */
import type {
  AdminTxStatus,
  AdminUserStatus,
  GiftcardStatus,
  KycStatus,
  KycTier,
  SupportPriority,
  SupportStatus,
} from '../contracts/types.js';
import { TIER_LABELS } from '../contracts/vocab.js';
import { humanize } from '../lib/format.js';
import { Badge } from './ui.js';
import type { Tone } from './ui.js';

const TIER_TONE: Record<KycTier, Tone> = {
  tier_0: 'neutral',
  tier_1: 'blue',
  tier_2: 'violet',
  tier_3: 'green',
};

export function TierBadge({ tier }: { tier: KycTier }): JSX.Element {
  /*
   * Through the shared label rather than string surgery on the enum.
   *
   * `tier.replace('tier_', 'Tier ')` printed "Tier 0" for an account that has verified nothing,
   * which is a rung that does not exist: an operator scanning a user list reads a column of
   * "Tier 0" as a population rather than as a queue of people who have not started. The label
   * table says "Not verified yet", which is what it is.
   */
  return <Badge tone={TIER_TONE[tier]}>{TIER_LABELS[tier]}</Badge>;
}

/**
 * Five states, five tones, and the tones say what the state costs the customer.
 *
 * Green is "nothing is blocked". Amber is a hold somebody chose and somebody can lift:
 * suspended and inactive both stop money moving and both are reversible from this console.
 * Red is the security lockout, which is the one an operator did not choose and the one a
 * customer is most likely to be on the phone about. Neutral is `closed`, which is terminal
 * and not an alarm: the customer did it, and there is nothing here to act on.
 *
 * Every case is spelled out rather than defaulted. `Record<AdminUserStatus, Tone>` is what
 * makes the contract widening this enum a compile error here instead of a blank chip beside
 * somebody's name in production.
 */
const USER_STATUS_TONE: Record<AdminUserStatus, Tone> = {
  active: 'green',
  suspended: 'amber',
  inactive: 'amber',
  locked: 'red',
  closed: 'neutral',
};

export function UserStatusBadge({ status }: { status: AdminUserStatus }): JSX.Element {
  return (
    <Badge tone={USER_STATUS_TONE[status]} dot>
      {humanize(status)}
    </Badge>
  );
}

const KYC_STATUS_TONE: Record<KycStatus, Tone> = {
  none: 'neutral',
  pending: 'amber',
  verified: 'green',
  rejected: 'red',
};

export function KycStatusBadge({ status }: { status: KycStatus }): JSX.Element {
  return <Badge tone={KYC_STATUS_TONE[status]}>{humanize(status)}</Badge>;
}

const TX_STATUS_TONE: Record<AdminTxStatus, Tone> = {
  settled: 'green',
  pending: 'amber',
  failed: 'red',
  reversed: 'neutral',
};

export function TxStatusBadge({ status }: { status: AdminTxStatus }): JSX.Element {
  return (
    <Badge tone={TX_STATUS_TONE[status]} dot>
      {humanize(status)}
    </Badge>
  );
}

const GIFTCARD_STATUS_TONE: Record<GiftcardStatus, Tone> = {
  pending: 'amber',
  approved: 'green',
  rejected: 'red',
};

export function GiftcardStatusBadge({ status }: { status: GiftcardStatus }): JSX.Element {
  return (
    <Badge tone={GIFTCARD_STATUS_TONE[status]} dot>
      {humanize(status)}
    </Badge>
  );
}

const SUPPORT_STATUS_TONE: Record<SupportStatus, Tone> = {
  open: 'violet',
  pending: 'amber',
  resolved: 'green',
  closed: 'neutral',
};

export function SupportStatusBadge({ status }: { status: SupportStatus }): JSX.Element {
  return (
    <Badge tone={SUPPORT_STATUS_TONE[status]} dot>
      {humanize(status)}
    </Badge>
  );
}

const SUPPORT_PRIORITY_TONE: Record<SupportPriority, Tone> = {
  low: 'neutral',
  normal: 'blue',
  high: 'amber',
  urgent: 'red',
};

export function SupportPriorityBadge({ priority }: { priority: SupportPriority }): JSX.Element {
  return <Badge tone={SUPPORT_PRIORITY_TONE[priority]}>{humanize(priority)}</Badge>;
}
